import { Player } from "@mediaspace/player";
import React from "react";
import { ChannelHeaderProps } from "./ChannelHeaderProps";
import { stripTags, translate, useMediaQuery } from "@mediaspace/shared/utils";
import { merge } from "ramda";
import Links from "./links/Links";
import { useTheme } from "@mediaspace/shared/styled";
import {
    StyledChannelHeaderWrapper,
    StyledBadge,
    StyledChannelHeader,
    StyledContentWrapper,
    StyledDescription,
    StyledImage,
    StyledLogo,
    StyledMediaWrapper,
    StyledTitle,
    StyledWrapper,
} from "./styled";
import ReactHtmlParser from "@mediaspace/shared/ui/html-react-parser";
import {TruncateWithTooltip} from "@mediaspace/shared/ui";
import { useContext } from "react";
import { ConfigContext } from "@mediaspace/shared/data-kms-config";

/**
 *  Channel page header
 */
export function ChannelHeader(props: ChannelHeaderProps) {
    const {
        image = "",
        bannerImage = "",
        logo = "",
        channelRank = "",
        name,
        description = "",
        media = null,
        playerConfig = null,
        onReadMore = undefined,
        ...linksProps
    } = props;

    const theme = useTheme();

    const isMediumScreen = useMediaQuery(
        theme.breakpoints.down(theme.breakpoints.values.lg)
    );

    const isSmallScreen = useMediaQuery(
        theme.breakpoints.down(theme.breakpoints.values.md)
    );

    // width/height is different in case of kaf
    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;

    const channelName = !isKaf ? name : translate('Media Gallery');

    // take only the first row in the description (split by "<br" to support all br tag syntaxes - <br> <br /> <br/>)
    const descriptionFirstLine = description.split("<br")[0];
    // strip &nbsp and html tags except a,i,b,u
    const descriptionPlain = stripTags(descriptionFirstLine, ['a', 'i', 'b', 'u']);

    let adaptedPlayerConfig = playerConfig;
    if (media && playerConfig) {
        const cfg = {
            ui: {
                ...playerConfig.ui,
                uiComponents: [
                    {
                        label: "niceComponent1", // just a name of your component
                        presets: ["Playback"], // the presets where you want to apply it
                        container: "BottomBarLeftControls",
                        get: () => undefined, // your component, in your case you just wanna return undefined
                        replaceComponent: "Forward", // name of component you wanna replace
                    },
                    {
                        label: "niceComponent2", // just a name of your component
                        presets: ["Playback"], // the presets where you want to apply it
                        container: "BottomBarLeftControls",
                        get: () => undefined, // your component, in your case you just wanna return undefined
                        replaceComponent: "Rewind", // name of component you wanna replace
                    },
                    {
                        label: "niceComponent3", // just a name of your component
                        presets: ["Playback"], // the presets where you want to apply it
                        container: "BottomBarRightControls",
                        get: () => undefined, // your component, in your case you just wanna return undefined
                        replaceComponent: "Settings", // name of component you wanna replace
                    },
                    {
                        label: "niceComponent4", // just a name of your component
                        presets: ["Playback"], // the presets where you want to apply it
                        container: "BottomBarRightControls",
                        get: () => undefined, // your component, in your case you just wanna return undefined
                        replaceComponent: "PictureInPicture", // name of component you wanna replace
                    },
                ],
            },
        };
        adaptedPlayerConfig = merge(playerConfig, cfg);
    }

    return (
        <StyledChannelHeader
            className={"kms-ds-channel-page-header"}
            style={{
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundImage: `url("${image}")`,
            }}
        >
            <StyledChannelHeaderWrapper
                isKaf={isKaf}
                className={"kms-ds-channel-page-header-wrapper"}
            >
                    {isSmallScreen && logo && <StyledLogo style={{backgroundImage: `url(${logo})`}} />}
                    <StyledWrapper isKaf={isKaf} className={"kms-ds-channel-page-header-content"}>
                        {!isSmallScreen && logo && <StyledLogo style={{backgroundImage: `url(${logo})`}} />}
                        <StyledContentWrapper className={"kms-ds-channel-page-header-text"}>
                                <TruncateWithTooltip text={channelName} lines={3}>
                                    <StyledTitle
                                        component={"h1"}
                                        variant={
                                            isSmallScreen
                                                ? "h3"
                                                : isMediumScreen
                                                    ? "h2"
                                                    : "h1"
                                        }
                                    >
                                        {channelName}
                                    </StyledTitle>
                                </TruncateWithTooltip>
                            {channelRank && <StyledBadge label={channelRank} />}
                            {!isSmallScreen && (
                                <StyledDescription
                                    className={"kms-ds-channel-page-header-text-description"}
                                    lines={3}
                                    showMoreTranslatedText={translate(
                                        "Read More"
                                    )}
                                    toggleMoreClassName={
                                        "channel-page-desc-more"
                                    }
                                    onToggle={
                                        onReadMore ? onReadMore : undefined
                                    }
                                    preventStateUpdate={!!onReadMore}
                                >
                                    {ReactHtmlParser(descriptionPlain)}
                                </StyledDescription>
                            )}
                            <Links {...linksProps} />
                        </StyledContentWrapper>
                        {media && playerConfig ? (
                            <StyledMediaWrapper isKaf={isKaf} className={"kms-ds-channel-page-header-video"}>
                                <Player
                                    media={media}
                                    config={adaptedPlayerConfig}
                                />
                            </StyledMediaWrapper>
                        ) : (
                            bannerImage && (
                                <StyledMediaWrapper isKaf={isKaf} className={"kms-ds-channel-page-header-image"}>
                                    <StyledImage src={bannerImage} role={"presentation"} />
                                </StyledMediaWrapper>
                            )
                        )}
                    </StyledWrapper>
            </StyledChannelHeaderWrapper>
        </StyledChannelHeader>
    );
}

export default ChannelHeader;
